import { createSignal, createMemo, Show, For, type Component } from "solid-js";
import { PanelLayout, type SubTab } from "@/shared/panel-base";
import { DataTable, type Column } from "@/shared/data-table";
import { createToolQuery } from "@/shared/query";
import { usePanel } from "@/shared/use-panel";
import { useJobEvents } from "@/shared/use-job-events";
import { useWs } from "@/shared/ws";
import { PanelBadge } from "@/shared/panel-primitives";
import { registerPanel } from "@/panel-registry";

// ─── Schémas (alignés sur RemoteWS.Plugin.Choco / ChocoTools.cs) ───────────
// choco_list / choco_search renvoient { count, rows: [{ name, version }] }
// choco_install / choco_upgrade / choco_uninstall renvoient { jobId, async, status }

interface ChocoPackage {
  name: string;
  version: string;
}

interface ListResult {
  count: number;
  rows: ChocoPackage[];
}

interface AsyncJobResult {
  jobId?: string;
  async?: boolean;
  status?: string;
}

// ─── Composant ─────────────────────────────────────────────────────────────

const ChocoPanel: Component = () => {
  const ws = useWs();
  const baseEnabled = () => ws.state.nativeAgentOnline && ws.state.supportApproved;
  const pluginVersion = () => ws.state.pluginVersions["choco"];
  const enabled = () => baseEnabled() && !!pluginVersion();

  // ─── État ──────────────────────────────────────────────────────────────
  const [filter, setFilter] = createSignal("");
  const [searchInput, setSearchInput] = createSignal("");
  const [searchTerm, setSearchTerm] = createSignal("");
  const [activeJobId, setActiveJobId] = createSignal<string | null>(null);
  const [jobLabel, setJobLabel] = createSignal<string>("");
  const [busyPackage, setBusyPackage] = createSignal<string | null>(null);

  // ─── Requêtes ──────────────────────────────────────────────────────────
  const listQuery = createToolQuery<ListResult>(() => ({
    tool: "choco_list",
    params: { limit: 100 },
    enabled: enabled(),
    refetchInterval: 0,
  }));

  const searchQuery = createToolQuery<ListResult>(() => ({
    tool: "choco_search",
    params: { query: searchTerm(), limit: 50 },
    enabled: enabled() && searchTerm().length > 0,
    refetchInterval: 0,
  }));

  const panel = usePanel(
    () => ({ isFetching: listQuery.isFetching, error: listQuery.error }),
    () => ({ isFetching: searchQuery.isFetching, error: searchQuery.error }),
  );
  const jobState = useJobEvents(() => activeJobId());

  // ─── Lignes ────────────────────────────────────────────────────────────
  const installedRows = createMemo<ChocoPackage[]>(() => {
    const all = listQuery.data?.rows ?? [];
    const q = filter().trim().toLowerCase();
    if (!q) return all;
    return all.filter((r) => r.name.toLowerCase().includes(q) || r.version.toLowerCase().includes(q));
  });

  const searchRows = createMemo<ChocoPackage[]>(() => searchQuery.data?.rows ?? []);

  const installedNames = createMemo<Set<string>>(
    () => new Set((listQuery.data?.rows ?? []).map((r) => r.name.toLowerCase())),
  );

  // ─── Suivi de job ──────────────────────────────────────────────────────
  let prevJobDone = false;
  function checkJobDone(): void {
    const js = jobState();
    const done = js?.status === "done" || js?.status === "error";
    if (done && !prevJobDone) {
      prevJobDone = true;
      const isError = js?.status === "error";
      setTimeout(async () => {
        await listQuery.refetch();
        if (searchTerm().length > 0) await searchQuery.refetch();
        setActiveJobId(null);
        setBusyPackage(null);
        panel.setActionStatus(
          isError
            ? { text: `Échec ${jobLabel()} : ${js?.error ?? "erreur inconnue"}`, level: "danger" }
            : { text: `${jobLabel()} terminé.`, level: "info" },
        );
        setTimeout(() => panel.setActionStatus(null), 3000);
        prevJobDone = false;
      }, 600);
    } else if (!done) {
      prevJobDone = false;
    }
  }

  // ─── Actions ───────────────────────────────────────────────────────────
  async function runJobTool(tool: string, pkg: ChocoPackage, label: string): Promise<void> {
    if (busyPackage()) return;
    setBusyPackage(pkg.name);
    setJobLabel(`${label} de ${pkg.name}`);
    panel.setActionStatus({ text: `${label} de "${pkg.name}" en cours…`, level: "info" });
    try {
      const params: Record<string, unknown> = { packageName: pkg.name };
      const res = await panel.tools.call<AsyncJobResult>(tool, params, {
        userConfirmedAt: new Date().toISOString(),
      });
      if (res?.jobId) {
        setActiveJobId(res.jobId);
      } else {
        // Réponse synchrone : on rafraîchit directement.
        setBusyPackage(null);
        panel.setActionStatus({ text: `${label} terminé.`, level: "info" });
        await listQuery.refetch();
        setTimeout(() => panel.setActionStatus(null), 2500);
      }
    } catch (err) {
      setBusyPackage(null);
      panel.setActionStatus({
        text: `Erreur ${label.toLowerCase()} : ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      });
    }
  }

  async function install(pkg: ChocoPackage): Promise<void> {
    const ok = await panel.confirm.confirm({
      title: `Installer — ${pkg.name}`,
      body: `Installer le paquet Chocolatey "${pkg.name}"${
        pkg.version ? ` (v${pkg.version})` : ""
      } sur ce host ?\nL'opération peut prendre plusieurs minutes.`,
      level: "warn",
      confirmLabel: "Installer",
      entityName: pkg.name,
    });
    if (!ok) return;
    await runJobTool("choco_install", pkg, "Installation");
  }

  async function upgrade(pkg: ChocoPackage): Promise<void> {
    const ok = await panel.confirm.confirm({
      title: `Mettre à jour — ${pkg.name}`,
      body: `Mettre à jour le paquet Chocolatey "${pkg.name}"${
        pkg.version ? ` (v${pkg.version})` : ""
      } sur ce host ?\nL'opération peut modifier le système et prendre plusieurs minutes.`,
      level: "warn",
      confirmLabel: "Mettre à jour",
      entityName: pkg.name,
    });
    if (!ok) return;
    await runJobTool("choco_upgrade", pkg, "Mise à jour");
  }

  async function uninstall(pkg: ChocoPackage): Promise<void> {
    const ok = await panel.confirm.confirm({
      title: `Désinstaller — ${pkg.name}`,
      body: `Désinstaller le paquet Chocolatey "${pkg.name}"${pkg.version ? ` (v${pkg.version})` : ""} de ce host ?`,
      level: "danger",
      confirmLabel: "Désinstaller",
      entityName: pkg.name,
    });
    if (!ok) return;
    await runJobTool("choco_uninstall", pkg, "Désinstallation");
  }

  function submitSearch(): void {
    setSearchTerm(searchInput().trim());
  }

  // ─── Colonnes ──────────────────────────────────────────────────────────
  const installedColumns: Column<ChocoPackage>[] = [
    { accessorKey: "name", header: "Paquet" },
    {
      accessorKey: "version",
      header: "Version",
      cell: (info) => <code style={{ "font-size": "11px" }}>{String(info.getValue() ?? "")}</code>,
    },
  ];

  const searchColumns: Column<ChocoPackage>[] = [
    { accessorKey: "name", header: "Paquet" },
    {
      accessorKey: "version",
      header: "Version",
      cell: (info) => <code style={{ "font-size": "11px" }}>{String(info.getValue() ?? "")}</code>,
    },
    {
      id: "state",
      header: "État",
      accessorFn: (r) => (installedNames().has(r.name.toLowerCase()) ? "installed" : ""),
      cell: (info) =>
        info.getValue() === "installed" ? (
          <PanelBadge intent="ok">Installé</PanelBadge>
        ) : (
          <PanelBadge intent="muted">—</PanelBadge>
        ),
    },
  ];

  // ─── Bloc de suivi de job ──────────────────────────────────────────────
  const jobBlock = (
    <Show when={jobState()}>
      {(js) => {
        checkJobDone();
        return (
          <div class="panel-detail-section" style={{ "margin-top": "8px" }}>
            <h4>
              {jobLabel()}{" "}
              <PanelBadge intent={js().status === "done" ? "ok" : js().status === "error" ? "danger" : "info"}>
                {js().status}
              </PanelBadge>
            </h4>
            <pre class="panel-job-log">
              <For each={js().logs}>{(line) => <div class="panel-job-log-line">{line}</div>}</For>
            </pre>
            <Show when={js().error}>
              <div class="panel-job-log-line" style={{ color: "var(--danger, #d33)" }}>
                {js().error}
              </div>
            </Show>
          </div>
        );
      }}
    </Show>
  );

  // ─── SubTabs ───────────────────────────────────────────────────────────
  const subtabs: SubTab[] = [
    {
      id: "installed",
      label: "📦 Installés",
      render: () => (
        <>
          <div class="panel-toolbar panel-toolbar--filter">
            <input
              class="panel-filter-input"
              placeholder="Filtrer (nom, version)…"
              value={filter()}
              onInput={(e) => setFilter(e.currentTarget.value)}
            />
            <button type="button" class="btn btn--xs btn--ghost" onClick={() => listQuery.refetch()} title="Rafraîchir">
              ↺ Rafraîchir
            </button>
            <Show when={listQuery.data}>
              <span style={{ "margin-left": "auto" }}>
                <PanelBadge intent="info">{installedRows().length} paquet(s)</PanelBadge>
              </span>
            </Show>
          </div>

          <DataTable<ChocoPackage>
            rows={installedRows()}
            columns={installedColumns}
            emptyMessage={enabled() ? "Aucun paquet installé." : "Plugin Chocolatey indisponible."}
            rowActions={(r) => {
              const busy = busyPackage();
              const isBusy = busy === r.name;
              return (
                <>
                  <button
                    class="btn btn--xs btn--ghost"
                    disabled={!!busy}
                    onClick={() => upgrade(r)}
                    title="Mettre à jour ce paquet"
                  >
                    {isBusy ? "…" : "Mettre à jour"}
                  </button>
                  <button
                    class="btn btn--xs btn--danger"
                    disabled={!!busy}
                    onClick={() => uninstall(r)}
                    title="Désinstaller ce paquet"
                  >
                    {isBusy ? "…" : "Désinstaller"}
                  </button>
                </>
              );
            }}
          />

          {jobBlock}
        </>
      ),
    },
    {
      id: "search",
      label: "🔍 Recherche",
      render: () => (
        <>
          <div class="panel-toolbar panel-toolbar--filter">
            <input
              class="panel-filter-input"
              placeholder="Rechercher un paquet Chocolatey…"
              value={searchInput()}
              onInput={(e) => setSearchInput(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitSearch();
              }}
            />
            <button
              type="button"
              class="btn btn--xs btn--primary"
              disabled={searchInput().trim().length === 0}
              onClick={submitSearch}
            >
              Rechercher
            </button>
            <Show when={searchQuery.data}>
              <span style={{ "margin-left": "auto" }}>
                <PanelBadge intent="info">{searchRows().length} résultat(s)</PanelBadge>
              </span>
            </Show>
          </div>

          <Show
            when={searchTerm().length > 0}
            fallback={
              <div class="panel-data-empty">
                Saisissez un terme puis « Rechercher » pour interroger le dépôt Chocolatey.
              </div>
            }
          >
            <DataTable<ChocoPackage>
              rows={searchRows()}
              columns={searchColumns}
              emptyMessage="Aucun paquet trouvé."
              rowActions={(r) => {
                const busy = busyPackage();
                const isBusy = busy === r.name;
                const alreadyInstalled = installedNames().has(r.name.toLowerCase());
                return (
                  <button
                    class="btn btn--xs btn--primary"
                    disabled={!!busy || alreadyInstalled}
                    onClick={() => install(r)}
                    title={alreadyInstalled ? "Déjà installé" : "Installer ce paquet"}
                  >
                    {isBusy ? "…" : alreadyInstalled ? "Installé" : "Installer"}
                  </button>
                );
              }}
            />
          </Show>

          {jobBlock}
        </>
      ),
    },
  ];

  return (
    <Show
      when={enabled() || !baseEnabled()}
      fallback={
        <PanelLayout title="Chocolatey" status={panel.status()}>
          <div class="panel-data-empty" style={{ opacity: "0.6" }}>
            Plugin Chocolatey non disponible sur ce host (choco.exe introuvable ou plugin non chargé).
          </div>
        </PanelLayout>
      }
    >
      <PanelLayout title="Chocolatey" subtabs={subtabs} status={panel.status()} />
    </Show>
  );
};

registerPanel("choco", ChocoPanel);
export default ChocoPanel;
